import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Progress,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";

const LARGE_HINT = 1500;

const TrainingExportTab = () => {
  const toast = useToast();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("confirmed");
  const [mode, setMode] = useState("yolo");
  const [job, setJob] = useState(null);
  const [downloadPercent, setDownloadPercent] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  };

  const pollJob = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const response = await fetch(`/api/infractions/training-export/${jobId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        setJob(data);
        if (data.status === "ready" || data.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (_) {
        // mantém a última barra até a próxima tentativa
      }
    }, 1000);
  };

  const startExport = async (confirmLarge) => {
    const response = await fetch("/api/infractions/training-export", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ startDate, endDate, status, mode, confirmLarge }),
    });
    const data = await response.json();
    if (response.status === 409 && data.requiresConfirmation) {
      const first = window.confirm(
        `Este período tem ${data.count} infrações. Exportações grandes demoram e ocupam bastante espaço. Deseja continuar?`
      );
      if (!first) return;
      const second = window.confirm(
        `Confirme novamente o download de ${data.count} infrações. O sistema continua respondendo enquanto o arquivo é gerado.`
      );
      if (!second) return;
      return startExport(true);
    }
    if (!response.ok) {
      throw new Error(data.message || "Erro ao iniciar exportação");
    }
    setDownloadPercent(null);
    setJob(data);
    pollJob(data.id);
  };

  const handleStart = async () => {
    if (!startDate || !endDate) {
      toast({ title: "Informe o período", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    try {
      const previewResponse = await fetch("/api/infractions/training-export/preview", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ startDate, endDate, status }),
      });
      const preview = await previewResponse.json();
      if (!previewResponse.ok) throw new Error(preview.message || "Erro ao contar infrações");
      if (!preview.count) {
        toast({ title: "Nenhuma infração nesse período", status: "info", duration: 3000, isClosable: true });
        return;
      }
      if (preview.count >= LARGE_HINT) {
        const first = window.confirm(
          `Este período tem ${preview.count} infrações. Exportações grandes demoram e ocupam bastante espaço. Deseja continuar?`
        );
        if (!first) return;
        const second = window.confirm(
          `Segunda confirmação: baixar ${preview.count} infrações para ${mode === "spreadsheet" ? "planilha" : "arquivo compactado"}?`
        );
        if (!second) return;
      }
      await startExport(preview.count >= LARGE_HINT);
    } catch (error) {
      toast({ title: "Exportação não iniciada", description: error.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const handleDownload = async () => {
    if (!job?.id) return;
    try {
      setDownloadPercent(0);
      const response = await fetch(`/api/infractions/training-export/${job.id}/download`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Falha no download");
      }
      const fileName = job.fileName || "infractions-export";
      const total = Number(response.headers.get("Content-Length")) || 0;

      if (window.showSaveFilePicker && response.body) {
        const handle = await window.showSaveFilePicker({ suggestedName: fileName });
        const writable = await handle.createWritable();
        const reader = response.body.getReader();
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          await writable.write(value);
          received += value.byteLength;
          if (total) setDownloadPercent(Math.round((received / total) * 100));
        }
        await writable.close();
      } else {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
      }
      setDownloadPercent(100);
      toast({ title: "Download concluído", status: "success", duration: 3000, isClosable: true });
    } catch (error) {
      if (error?.name === "AbortError") return;
      setDownloadPercent(null);
      toast({ title: "Erro no download", description: error.message, status: "error", duration: 5000, isClosable: true });
    }
  };

  const busy = job && (job.status === "queued" || job.status === "running");

  return (
    <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="md" boxShadow="sm" maxW="760px">
      <Heading size="md" mb={2}>Exportar</Heading>
      <Text fontSize="sm" color="gray.600" mb={4}>
        Escolha o período e o que baixar: só as informações, só as fotos, ou fotos e caixas de detecção no formato de treino.
      </Text>

      <VStack align="stretch" spacing={4}>
        <Stack direction={{ base: "column", md: "row" }} spacing={3}>
          <FormControl>
            <FormLabel fontSize="sm">De</FormLabel>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Até</FormLabel>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">Status</FormLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="confirmed">Confirmadas</option>
              <option value="pending">Pendentes</option>
              <option value="rejected">Rejeitadas</option>
              <option value="all">Todas</option>
            </Select>
          </FormControl>
        </Stack>

        <FormControl>
          <FormLabel fontSize="sm">O que baixar</FormLabel>
          <RadioGroup value={mode} onChange={setMode}>
            <Stack spacing={2}>
              <Radio value="spreadsheet">Só as informações (planilha Excel)</Radio>
              <Radio value="photos">Só as fotos</Radio>
              <Radio value="yolo">Tudo no formato de treino (fotos + caixas YOLO)</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Confirmadas são as melhores para treino: um humano já validou a detecção. A geração roda em segundo plano e não trava o restante do sistema.
        </Alert>

        <Button colorScheme="teal" onClick={handleStart} isLoading={busy} loadingText="Gerando arquivo">
          Iniciar exportação
        </Button>

        {job && (
          <Box>
            <Text fontSize="sm" mb={2}>
              {job.phase} {job.total ? `(${job.processed || 0} de ${job.total})` : ""}
            </Text>
            <Progress
              value={job.status === "error" ? 100 : (job.percent || 0)}
              colorScheme={job.status === "error" ? "red" : job.status === "ready" ? "green" : "teal"}
              borderRadius="md"
              hasStripe={busy}
              isAnimated={busy}
            />
            {job.status === "error" && (
              <Text color="red.500" fontSize="sm" mt={2}>{job.error}</Text>
            )}
            {job.status === "ready" && (
              <Button mt={3} colorScheme="green" onClick={handleDownload}>
                Baixar {job.fileName}
              </Button>
            )}
            {downloadPercent != null && downloadPercent < 100 && (
              <Box mt={3}>
                <Text fontSize="sm" mb={1}>Baixando arquivo… {downloadPercent}%</Text>
                <Progress value={downloadPercent} colorScheme="blue" borderRadius="md" />
              </Box>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default TrainingExportTab;
